import React, { useState, useEffect } from 'react';
import { FaSearch, FaFilter, FaCheckCircle, FaClock, FaDollarSign } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import './Payments.css';
import Pagination from '../Common/Pagination';

const PaymentsList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState(''); // New state for real-time typing
  const [statusFilter, setStatusFilter] = useState('all');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  // Debounce logic for search
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(inputValue);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500); // 500ms debounce

    return () => clearTimeout(handler);
  }, [inputValue]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    platformCommission: 0,
    pendingPayouts: 0,
    activeDisputes: 0
  });

  const fetchPayments = async () => {
    if (isFirstLoad) setLoading(true); // No flash on re-fetch
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter === 'all' ? undefined : statusFilter.toUpperCase(),
        search: searchTerm || undefined // Ensure search is passed to API
      };
      
      const [paymentsRes, statsRes] = await Promise.all([
        api.get(ENDPOINTS.PAYMENTS.base, { params }),
        api.get(ENDPOINTS.PAYMENTS.stats)
      ]);

      const paymentsData = paymentsRes.data || [];
      const statsData = statsRes.data || statsRes;

      setPayments(paymentsData.data || paymentsData);
      setPagination(prev => ({ ...prev, total: paymentsData.meta?.total || paymentsRes.total || 0 }));
      setStats({
        totalRevenue: statsData.totalRevenue || 0,
        platformCommission: statsData.platformCommission || 0,
        pendingPayouts: statsData.pendingPayouts || 0,
        activeDisputes: statsData.activeDisputes || 0
      });
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    } finally {
      setLoading(false);
      setIsFirstLoad(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [pagination.page, statusFilter, searchTerm]);

  if (loading && isFirstLoad) {
    return (
      <div className="loading-spinner-container">
        <div className="modern-spinner"></div>
        <p className="loading-text">Fetching payments...</p>
      </div>
    );
  }

  return (
    <div className="payments-page">
      <div className="page-header">
        <div>
          <h1>Payments</h1>
          <p className="page-subtitle">Track all payment transactions</p>
        </div>
      </div>

      {/* Premium Stats Summary */}
      {/* <div className="payments-stats-premium">
        <div className="stat-card-premium">
          <div className="stat-icon-wrapper revenue">
            <FaDollarSign />
          </div>
          <div className="stat-details">
            <h3>${Number(stats.totalRevenue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</h3>
            <p>Total Revenue (Funds Collected)</p>
          </div>
        </div>
        
        <div className="stat-card-premium">
          <div className="stat-icon-wrapper commission">
            <FaCheckCircle />
          </div>
          <div className="stat-details">
            <h3>${Number(stats.platformCommission).toLocaleString(undefined, { minimumFractionDigits: 2 })}</h3>
            <p>Platform Commission (Earned)</p>
          </div>
        </div>

        <div className="stat-card-premium">
          <div className="stat-icon-wrapper pending">
            <FaClock />
          </div>
          <div className="stat-details">
            <h3>{stats.pendingPayouts}</h3>
            <p>Pending Transactions</p>
          </div>
        </div>
      </div> */}

      {/* Unified List Header & Search */}
      <div className="content-card filters-container-premium">
        <div className="filters-header-row" style={{ padding: '0 16px' }}>
            <div className="list-title-group">
                <h3 className="list-title">All Payments</h3>
                <span className="list-count">{pagination.total} transactions tracked</span>
            </div>
            
            <div className="d-flex align-items-center gap-3 flex-grow-1" style={{ maxWidth: '600px', justifyContent: 'flex-end' }}>
                <div className="search-bar-premium" style={{ flex: 1 }}>
                  <FaSearch className="search-icon" />
                  <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Search by ID or Name..."
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      className="search-input"
                      style={{ width: '100%', paddingRight: '30px' }}
                    />
                    {inputValue && (
                      <button 
                        onClick={() => setInputValue('')}
                        style={{ position: 'absolute', right: '8px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0', fontSize: '16px', display: 'flex', alignItems: 'center' }}
                        title="Clear search"
                      >
                        &#x2715;
                      </button>
                    )}
                  </div>
                </div>

                <div className="filter-dropdown-premium">
                    <FaFilter className="filter-icon" />
                    <select 
                      value={statusFilter} 
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPagination(prev => ({ ...prev, page: 1 }));
                      }}
                      className="filter-select-minimal"
                    >
                      <option value="all">All Status</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                      <option value="failed">Failed</option>
                    </select>
                </div>
            </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="content-card table-card-premium" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>

        <div className="table-responsive">
          <table className="table table-hover align-middle custom-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Customer</th>
                <th>Provider</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td>
                    <span className="booking-id">BID{payment.booking?.id || 'N/A'}</span>
                  </td>
                  <td>{payment.booking?.customer?.firstName} {payment.booking?.customer?.lastName}</td>
                  <td>{payment.booking?.provider?.firstName} {payment.booking?.provider?.lastName}</td>
                  <td>
                    <span className="amount">${Number(payment.amount).toFixed(2)}</span>
                    {payment.booking?.isDeposit && <span className="deposit-label" style={{ fontSize: '0.75rem', color: '#10b981', marginLeft: '5px', fontWeight: '600' }}>(Deposit)</span>}
                  </td>
                  <td>
                    {(payment.status || '').toUpperCase() === 'COMPLETED' ? (
                      <span className="badge badge-success">
                        <FaCheckCircle /> Completed
                      </span>
                    ) : (
                      <span className="badge badge-warning">
                        <FaClock /> {payment.status}
                      </span>
                    )}
                  </td>
                  <td>{new Date(payment.createdAt).toLocaleDateString()}</td>
                  <td>
                    <code className="transaction-id">{payment.stripePaymentIntentId || payment.transactionId || 'Manual'}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <Pagination 
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        />
      </div>
    </div>
  );
};

export default PaymentsList;